import * as React from 'react';

import { cn } from '../lib/cn';

export type DiffMode = 'inline' | 'split';

export interface DiffViewProps extends React.HTMLAttributes<HTMLDivElement> {
  before: string;
  after: string;
  mode?: DiffMode;
  language?: string;
  ariaLabel?: string;
}

type Op = { kind: 'eq' | 'del' | 'add'; text: string };

/**
 * LCS line diff — produces a sequence of equal / del / add operations.
 * Lightweight (no external dep), suitable for short-to-medium diffs.
 */
function lcsDiff(beforeText: string, afterText: string): Op[] {
  const a = beforeText.split('\n');
  const b = afterText.split('\n');
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops: Op[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      ops.push({ kind: 'eq', text: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ kind: 'del', text: a[i] });
      i++;
    } else {
      ops.push({ kind: 'add', text: b[j] });
      j++;
    }
  }
  while (i < m) ops.push({ kind: 'del', text: a[i++] });
  while (j < n) ops.push({ kind: 'add', text: b[j++] });
  return ops;
}

const lineBase =
  'whitespace-pre-wrap break-words px-3 py-0.5 font-mono text-[12.5px] leading-relaxed';

const opClass = (kind: Op['kind']) =>
  kind === 'add'
    ? 'bg-[color-mix(in_srgb,var(--color-success)_18%,transparent)] text-[var(--color-fg)]'
    : kind === 'del'
      ? 'bg-[color-mix(in_srgb,var(--color-danger)_18%,transparent)] text-[var(--color-fg)]'
      : 'text-[var(--color-fg-secondary)]';

const sign = (kind: Op['kind']) => (kind === 'add' ? '+' : kind === 'del' ? '-' : ' ');

export const DiffView = React.forwardRef<HTMLDivElement, DiffViewProps>(
  ({ before, after, mode = 'inline', language, ariaLabel, className, ...props }, ref) => {
    const ops = React.useMemo(() => lcsDiff(before, after), [before, after]);

    const Frame = (
      <div
        ref={ref}
        role="group"
        aria-label={ariaLabel ?? 'Diff view'}
        className={cn(
          'overflow-hidden rounded-lg border bg-[var(--color-neutral-900)] border-[var(--color-border)]',
          className,
        )}
        {...props}
      >
        {language && (
          <div className="border-b border-[var(--color-border)] bg-[var(--color-neutral-800)] px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--color-fg-muted)]">
            {language}
          </div>
        )}
        {mode === 'inline' ? (
          <div className="overflow-x-auto py-2">
            {ops.map((op, idx) => (
              <div key={idx} className={cn(lineBase, opClass(op.kind))}>
                <span className="select-none pr-2 text-[var(--color-fg-muted)]">{sign(op.kind)}</span>
                {op.text || ' '}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-[var(--color-border)]">
            <div className="py-2">
              {ops.map((op, idx) =>
                op.kind === 'add' ? (
                  <div key={idx} className={cn(lineBase, 'opacity-40')}>&nbsp;</div>
                ) : (
                  <div key={idx} className={cn(lineBase, opClass(op.kind === 'eq' ? 'eq' : 'del'))}>
                    <span className="select-none pr-2 text-[var(--color-fg-muted)]">
                      {op.kind === 'del' ? '-' : ' '}
                    </span>
                    {op.text || ' '}
                  </div>
                ),
              )}
            </div>
            <div className="py-2">
              {ops.map((op, idx) =>
                op.kind === 'del' ? (
                  <div key={idx} className={cn(lineBase, 'opacity-40')}>&nbsp;</div>
                ) : (
                  <div key={idx} className={cn(lineBase, opClass(op.kind === 'eq' ? 'eq' : 'add'))}>
                    <span className="select-none pr-2 text-[var(--color-fg-muted)]">
                      {op.kind === 'add' ? '+' : ' '}
                    </span>
                    {op.text || ' '}
                  </div>
                ),
              )}
            </div>
          </div>
        )}
      </div>
    );

    return Frame;
  },
);
DiffView.displayName = 'DiffView';
